import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle 
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Mail } from 'lucide-react'
import { getStepParamsSchema } from '../../lib/workflow/validation'
import type { WorkflowStep, StepType } from '../../types/workflow'

interface StepConfigFormProps {
  step: WorkflowStep | null
  isOpen: boolean
  onClose: () => void
  onSave: (stepId: string, updates: Partial<WorkflowStep>) => void
}

export function StepConfigForm({ step, isOpen, onClose, onSave }: StepConfigFormProps) {
  const schema = step ? getStepParamsSchema(step.step_type) : null

  // Ensure proper default values, especially for Select components
  const getDefaultValues = (step: WorkflowStep) => {
    const params = { ...step.params } as any

    // For scroll step, ensure direction has a valid value
    if (step.step_type === 'scroll' && (!params.direction || params.direction === '')) {
      params.direction = 'down'
    }

    return params
  }

  const form = useForm<any>({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues: step ? getDefaultValues(step) : {},
    mode: 'onChange'
  })

  // Reset form when step changes
  React.useEffect(() => {
    if (step) {
      form.reset(getDefaultValues(step))
    }
  }, [step, form])

  if (!step) return null

  const handleSave = (data: any) => {
    // Transform URL if needed (add https:// if missing)
    if (step.step_type === 'navigate_url' && data.url && !data.url.startsWith('http://') && !data.url.startsWith('https://')) {
      data.url = `https://${data.url}`
    }

    onSave(step.id, { params: data })
    onClose()
  }

  const getStepDisplayName = (stepType: StepType): string => {
    switch (stepType) {
      case 'mouse_click': return 'Click'
      case 'mouse_double_click': return 'Double Click'
      case 'type_text': return 'Type Text'
      case 'navigate_url': return 'Navigate to URL'
      case 'take_screenshot': return 'Take Screenshot'
      case 'scroll': return 'Scroll'
      case 'keyboard_shortcut': return 'Keyboard Shortcut'
      case 'drag_drop': return 'Drag & Drop'
      case 'task': return 'Task'
      case 'verify': return 'Verify'
      case 'notify': return 'Notify'
      default: return stepType
    }
  }

  const renderFormFields = () => {
    switch (step.step_type) {
      case 'mouse_click':
      case 'mouse_double_click':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="selector">CSS Selector</Label>
              <Input
                id="selector"
                placeholder="e.g., #button-id, .class-name, [data-testid='submit']"
                {...form.register('selector')}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty to use coordinates instead
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="x">X Coordinate</Label>
                <Input
                  id="x"
                  type="number"
                  placeholder="0"
                  {...form.register('x', { valueAsNumber: true })}
                />
              </div>
              <div>
                <Label htmlFor="y">Y Coordinate</Label>
                <Input
                  id="y"
                  type="number"
                  placeholder="0"
                  {...form.register('y', { valueAsNumber: true })}
                />
              </div>
            </div>
            
            {step.step_type === 'mouse_click' && (
              <div>
                <Label htmlFor="waitFor">Wait Time (ms)</Label>
                <Input
                  id="waitFor"
                  type="number"
                  placeholder="1000"
                  {...form.register('waitFor', { valueAsNumber: true })}
                />
              </div>
            )}
          </div>
        )

      case 'type_text':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="selector">CSS Selector *</Label>
              <Input
                id="selector"
                placeholder="e.g., input[name='email'], #search-box"
                {...form.register('selector')}
              />
            </div>
            
            <div>
              <Label htmlFor="text">Text to Type *</Label>
              <Textarea
                id="text"
                placeholder="Enter the text to type..."
                {...form.register('text')}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="clearFirst"
                checked={form.watch('clearFirst')}
                onCheckedChange={(checked) => form.setValue('clearFirst', checked)}
              />
              <Label htmlFor="clearFirst">Clear field before typing</Label>
            </div>
          </div>
        )

      case 'navigate_url':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com"
                {...form.register('url')}
              />
            </div>
            
            <div>
              <Label htmlFor="waitForLoad">Wait for Load (ms)</Label>
              <Input
                id="waitForLoad"
                type="number"
                placeholder="5000"
                {...form.register('waitForLoad', { valueAsNumber: true })}
              />
            </div>
          </div>
        )

      case 'scroll':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="direction">Direction *</Label>
              <Select
                value={form.watch('direction') || 'down'}
                onValueChange={(value) => form.setValue('direction', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select direction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="up">Up</SelectItem>
                  <SelectItem value="down">Down</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="amount">Amount (pixels) *</Label>
              <Input
                id="amount"
                type="number"
                placeholder="300"
                {...form.register('amount', { valueAsNumber: true })}
              />
            </div>
            
            <div>
              <Label htmlFor="selector">Element Selector</Label>
              <Input
                id="selector"
                placeholder="Leave empty to scroll the page"
                {...form.register('selector')}
              />
            </div>
          </div>
        )

      case 'keyboard_shortcut':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="keys">Key Combination *</Label>
              <Input
                id="keys"
                placeholder="e.g., Ctrl+C, Alt+Tab, Enter"
                {...form.register('keys')}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use + to combine keys (Ctrl+C) or single keys (Enter, Escape)
              </p>
            </div>
          </div>
        )

      case 'take_screenshot':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="filename">Filename</Label>
              <Input
                id="filename"
                placeholder="Leave empty for auto-generated name"
                {...form.register('filename')}
              />
            </div>
          </div>
        )

      case 'set_clipboard':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="text">Text *</Label>
              <Textarea
                id="text"
                placeholder="Text to copy to clipboard"
                {...form.register('text')}
              />
            </div>
          </div>
        )

      case 'drag_drop':
        return (
          <div className="space-y-4">
            <div>
              <h4 className="font-medium">Source</h4>
              <div className="space-y-2">
                <Input
                  placeholder="Source selector"
                  {...form.register('sourceSelector')}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Source X"
                    {...form.register('sourceX', { valueAsNumber: true })}
                  />
                  <Input
                    type="number"
                    placeholder="Source Y"
                    {...form.register('sourceY', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <h4 className="font-medium">Target</h4>
              <div className="space-y-2">
                <Input
                  placeholder="Target selector"
                  {...form.register('targetSelector')}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Target X"
                    {...form.register('targetX', { valueAsNumber: true })}
                  />
                  <Input
                    type="number"
                    placeholder="Target Y"
                    {...form.register('targetY', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>
          </div>
        )

      case 'task':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="description">Task Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe what the AI should accomplish (e.g., 'Fill out the contact form with my information')"
                {...form.register('description')}
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Describe the task in natural language. The AI will interpret and execute this instruction.
              </p>
            </div>
          </div>
        )

      case 'verify':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="description">Verification Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe what should be verified (e.g., 'Check that the form was submitted successfully')"
                {...form.register('description')}
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Describe the expected state or condition that should be validated at this point.
              </p>
            </div>
          </div>
        )

      case 'notify':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Email Notification</span>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                When this workflow completes successfully, a notification email will be sent to your account email address.
              </p>
            </div>
            <div>
              <Label htmlFor="message">Custom Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Add a custom message to include in the notification email..."
                {...form.register('message')}
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Optional custom message to include in the notification email.
              </p>
            </div>
          </div>
        )

      default:
        return (
          <div className="text-center py-8 text-muted-foreground">
            <p>No configuration needed for this step type</p>
          </div>
        )
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Configure Step
            <Badge variant="outline">{getStepDisplayName(step.step_type)}</Badge>
          </SheetTitle>
          <SheetDescription>
            Configure the parameters for this workflow step
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6 mt-6">
          {renderFormFields()}

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={(() => {
                // For navigate_url, allow if URL field has content
                if (step.step_type === 'navigate_url') {
                  const urlValue = form.watch('url')
                  return !urlValue || urlValue.trim().length === 0
                }
                // For other step types, use standard validation
                return !form.formState.isValid
              })()}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
